import { apiClient } from '../client';

// ── Prompt/config interfaces ──────────────────────────────────────────────────

export interface MessagePromptCfg {
  systemMessage: string;
  baseContext: string[];
  instructions: {
    email: string[];
    'linkedin-connection': string[];
    'linkedin-inmail': string[];
  };
}

export interface CampaignMessageTemplateCfg {
  linkedinConn: string;
  linkedinInmail: string[];
  inmailSubjects: string[];
  linkedinFollowup: string[];
  email: string[];
  emailSubjects: string[];
}

export interface FaAnalysisPromptCfg {
  systemMessage: string;
  userPrompt: string[];
}

export interface ProspectResearchPromptCfg {
  // NOTE: the live active config uses `systemMessage`. The db_loader_new.py seed
  // and older history rows used `systemMsg` — that key was migrated. We bind to
  // the active shape; an old-schema version can still be edited via the Raw JSON tab.
  systemMessage: string;
  preAmbleWLinkedIn: string;
  preAmbleNoLinkedIn: string;
  userPrompt: string[];
}

// AskArchie coaching RAG: system prompt + per-archetype/experience coaching
// notes, response-length presets (5/3/1 min), RAG knobs, and model identifiers.
// Keys are preserved byte-for-byte from the backend.
export interface CoachingSettingsCfg {
  basePrompt: string[];
  ARCHETYPE_NOTES: Record<string, string[]>;
  persona_adaptation: string[];
  framework_names: string[];
  RAG_MATCH_THRESHOLD: number;
  RAG_QUERY_LIMIT: number;
  RAG_CONTENT_INTRO: string[];
  ELEVENLABS_MODEL: string;
  EXPERIENCE_NOTES: Record<string, string[]>;
  LENGTH_HINTS: Record<string, string>;
  CLAUDE_MODEL: string;
  LENGTH_DIRECTIVES: Record<string, string>;
  personalization: string[];
  LENGTH_TOKENS: Record<string, number>;
  OPENAI_EMBEDDING_MODEL: string;
  ELEVENLABS_VOICE_ID: string;
}

export interface ExemplarScoringConfigCfg {
  // The three weights should sum to 1.0; UI blocks Save until they do.
  distanceWeight: number;
  careerWeight: number;
  roleWeight: number;
  // Integer cap on tenure used in career scoring.
  tenureCap: number;
  // NAICS industry-similarity decay factor (0..1).
  naics_decay: number;
  // Maximum search distance in miles (?) — clarify with backend if used elsewhere.
  maxDistance: number;
  // Role-keyword → seniority level (int). Taxonomy is fixed in the structured
  // editor; add/remove/rename via the Raw JSON tab.
  seniorityMap: Record<string, number>;
}

export type ConfigName =
  | 'message_prompt'
  | 'campaign_message_template'
  | 'fa_analysis_prompt'
  | 'prospect_research_prompt'
  | 'exemplar_scoring_config'
  | 'coaching_settings';

export type CfgUnion =
  | MessagePromptCfg
  | CampaignMessageTemplateCfg
  | FaAnalysisPromptCfg
  | ProspectResearchPromptCfg
  | ExemplarScoringConfigCfg
  | CoachingSettingsCfg;

type ConfigGetterMap = {
  message_prompt: MessagePromptCfg;
  campaign_message_template: CampaignMessageTemplateCfg;
  fa_analysis_prompt: FaAnalysisPromptCfg;
  prospect_research_prompt: ProspectResearchPromptCfg;
  exemplar_scoring_config: ExemplarScoringConfigCfg;
  coaching_settings: CoachingSettingsCfg;
};

// ── Config history ──────────────────────────────────────────────────────────
// One row of rm_config. A "save" flips the current row to is_active=false and
// inserts a new is_active=true row, so history is all rows for a given type
// (newest = highest config_id). Shape confirmed against a live
// GET /adm/get_config_history/{name} response (a bare array of these rows):
//   { config_id: int, type, is_active: bool, created: ISO-8601, description, cfg }
// There is no author/updated_by field. `cfg` is the parsed config object, so it
// can be loaded straight back into the editor for Restore. Fields kept optional
// so a missing/renamed column degrades to "—" rather than crashing.
export interface ConfigHistoryEntry {
  config_id?: number | string;
  type?: string;
  cfg?: CfgUnion;
  description?: string;
  is_active?: boolean;
  created?: string;
}

export interface SaveConfigResult {
  ok?: boolean;
  config_id?: number | string;
  // On validation failure the backend returns errors and does not save.
  errors?: string[] | Record<string, unknown>;
}

// ── User interface ────────────────────────────────────────────────────────────

export interface User {
  id: string;
  full_name: string;
  org_name: string;
  email: string;
  active: boolean;
  status: {
    fa_assessment: number;
    fa_analysis: number;
    prospect_search: number;
    prospect_research: number;
    prospect_messages: number;
    subscription: {
      is_subscribed: boolean;
      available_credits: number;
    };
  };
}

// ── COGS: raw API logs + token usage (Sync source) ────────────────────────────
// The COGS explorer syncs a user's real spend from two backend endpoints and joins
// them by `id`:
//   - get_api_logs: one row per API call (model via api_name, action via api_url,
//     request/response bodies). NO token counts on the row.
//   - get_token_usage: per-call token counts for one canonical model name, each row
//     carrying the `id` that matches an api_logs row. Only these three api_names are
//     supported: 'gemini-3.1-flash-lite', 'claude-sonnet-4-6', 'text-embedding-3-small'.
// Normalization/join lives in src/lib/cogs/sync.ts.
export interface ApiLogRow {
  // Join key. Backend is renaming `id` → `log_id`; we read `log_id ?? id` so it
  // works across the transition.
  log_id?: number;
  id?: number;
  user_id: string;
  api_name: string; // model id or vendor operation label (e.g. 'Apollo person enrich by name')
  api_url: string; // the reliable action/vendor classifier
  api_request: string; // may be plaintext or a JSON string
  api_response: string; // may be plaintext, JSON (with a usage block), or binary (TTS audio)
  api_status: string;
  created: string;
}

// Token-count fields vary by provider; all values arrive as strings.
export interface TokenUsageRow {
  log_id?: number; // matches ApiLogRow.log_id (formerly `id`)
  id?: number;
  created?: string;
  // Gemini
  prompt_token_count?: string;
  candidates_token_count?: string;
  thoughts_token_count?: string;
  total_token_count?: string;
  // Claude
  input_tokens?: string;
  output_tokens?: string;
  // OpenAI embeddings
  prompt_tokens?: string;
  total_tokens?: string;
}

export interface TokenUsageResponse {
  user_id: string;
  api_name: string;
  data: TokenUsageRow[];
}

/** The only api_name values get_token_usage supports today. */
export const TOKEN_USAGE_API_NAMES = [
  'gemini-3.1-flash-lite',
  'claude-sonnet-4-6',
  'text-embedding-3-small',
] as const;

// ── Campaign interfaces ───────────────────────────────────────────────────────

export interface Campaign {
  id: number;
  name: string;
  description?: string;
  slug?: string;
  schema?: {
    message_template?: unknown;
    [key: string]: unknown;
  };
}

export interface AddCampaignRequest {
  name: string;
  description?: string;
  schema?: unknown;
}

export interface AddCampaignResult {
  id?: number;
  campaign_id?: number;
  slug?: string;
  ok?: boolean;
}

export interface UpdateCampaignRequest {
  campaign_id: number | string;
  name?: string;
  description?: string;
  schema?: unknown;
}

export interface UpdateCampaignResult {
  ok?: boolean;
  campaign_id?: number;
}

// ── Coaching history ──────────────────────────────────────────────────────────
// One row of AskArchie coaching history: a single question→answer turn.
// Shape confirmed against a live GET /adm/get_all_coaching_history response
// (a bare array of these rows, not sorted; no pagination or filters).
// There is no session id — the UI groups turns into sessions by inactivity gap.

export interface CoachingChunkUsed {
  chunk_id: string;
  // Cosine similarity of the retrieved RAG chunk (0..1).
  similarity: number;
}

export interface CoachingHistoryRow {
  history_id: number;
  user_id: string;
  user_content: string;
  // May contain raw template tokens like {ADVISORNAME}.
  assistant_content: string;
  chunks_used: CoachingChunkUsed[];
  created: string;
  api_log_id: number | null;
}

// ── Admin service ─────────────────────────────────────────────────────────────

export const adminService = {
  getConfig: <K extends ConfigName>(name: K): Promise<ConfigGetterMap[K]> =>
    apiClient<ConfigGetterMap[K]>(`/get_config/${encodeURIComponent(name)}`),

  getConfigHistory: (name: ConfigName) =>
    apiClient<ConfigHistoryEntry[]>(
      `/get_config_history/${encodeURIComponent(name)}`,
    ),

  // Backend runs basic key/variable validation and refuses to save on error;
  // apiClient surfaces that as a thrown ApiError with the validation message.
  saveConfig: (config_name: ConfigName, config: CfgUnion) =>
    apiClient<SaveConfigResult>('/save_config', {
      method: 'POST',
      body: JSON.stringify({ config_name, config }),
    }),

  addNewAdmin: (full_name: string, email: string, password: string) =>
    apiClient<{ ok?: boolean }>('/add_new', {
      method: 'POST',
      body: JSON.stringify({ full_name, email, password }),
    }),

  disableAdmin: (email: string) =>
    apiClient<{ ok?: boolean }>('/disable_admin', {
      method: 'POST',
      body: JSON.stringify({ email }),
    }),

  enableAdmin: (email: string) =>
    apiClient<{ ok?: boolean }>('/enable_admin', {
      method: 'POST',
      body: JSON.stringify({ email }),
    }),

  getUsers: () =>
    apiClient<User[]>('/get_users'),

  disableUser: (user_id: string) =>
    apiClient<{ ok?: boolean }>('/disable_user', {
      method: 'POST',
      body: JSON.stringify({ user_id }),
    }),

  enableUser: (user_id: string) =>
    apiClient<{ ok?: boolean }>('/enable_user', {
      method: 'POST',
      body: JSON.stringify({ user_id }),
    }),

  addCredits: (user_id: string, num_credits: number) =>
    apiClient<{ available_credits: number }>('/add_credits', {
      method: 'POST',
      body: JSON.stringify({ user_id, num_credits }),
    }),

  // COGS sync sources (see src/lib/cogs/sync.ts for the join/normalize logic).
  getApiLogs: (user_id: string, offset: number) =>
    apiClient<ApiLogRow[]>('/get_api_logs', {
      method: 'POST',
      body: JSON.stringify({ user_id, offset }),
    }),

  getTokenUsage: (user_id: string, api_name: string) =>
    apiClient<TokenUsageResponse>('/get_token_usage', {
      method: 'POST',
      body: JSON.stringify({ user_id, api_name }),
    }),

  getCampaigns: () =>
    apiClient<Campaign[]>('/get_campaigns'),

  getAllCoachingHistory: () =>
    apiClient<CoachingHistoryRow[]>('/get_all_coaching_history'),

  addCampaign: (req: AddCampaignRequest) =>
    apiClient<AddCampaignResult>('/add_campaign', {
      method: 'POST',
      body: JSON.stringify(req),
    }),

  updateCampaign: (req: UpdateCampaignRequest) =>
    apiClient<UpdateCampaignResult>('/update_campaign', {
      method: 'POST',
      body: JSON.stringify(req),
    }),
};
